import { ReactNode, useEffect, useState } from "react";
import AnimatedWaves from "./AnimatedWaves";

interface HeroSectionProps {
  image: string;
  webpImage?: string;
  /** srcSet like "img-640.avif 640w, img-1280.avif 1280w" */
  avifSrcSet?: string;
  webpSrcSet?: string;
  sizes?: string;
  /**
   * Optionales Hintergrund-Video (same-origin, stummgeschaltet).
   * Wird erst NACH dem ersten Paint über das Hero-Bild gelegt, damit der
   * LCP-Wert unverändert vom Bild kommt. Auf allen Gerätegrößen sichtbar;
   * nur bei prefers-reduced-motion bleibt es aus.
   */
  videoSrc?: string;
  title: string;
  subtitle?: string;
  children?: ReactNode;
  overlay?: boolean;
  height?: string;
  priority?: boolean;
}

/**
 * Hero-Sektion — 100 % statisches Rendering.
 * Kein Framer-Motion-Wrapper mehr um Bild oder H1: alle Parallax-, Scale-
 * und Opacity-Transitions wurden entfernt, um LCP-Renderverzögerung
 * (blockiertes Paint durch transform/composite) zu vermeiden.
 */
const HeroSection = ({
  image,
  webpImage,
  avifSrcSet,
  webpSrcSet,
  sizes = "100vw",
  videoSrc,
  title,
  subtitle,
  children,
  overlay = true,
  height = "h-[75vh] sm:h-[85vh] lg:h-[95vh]",
  priority = false,
}: HeroSectionProps) => {
  const [showVideo, setShowVideo] = useState(false);
  const [videoReady, setVideoReady] = useState(false);

  useEffect(() => {
    if (!videoSrc) return;
    if (typeof window === "undefined") return;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reducedMotion) return;

    let cancelled = false;
    const start = () => !cancelled && setShowVideo(true);
    const idle = (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number })
      .requestIdleCallback;
    const t = idle ? idle(start, { timeout: 2500 }) : window.setTimeout(start, 1200);

    return () => {
      cancelled = true;
      if (!idle) window.clearTimeout(t);
    };
  }, [videoSrc]);

  return (
    <section
      className={`relative ${height} flex items-center justify-center overflow-hidden`}
    >
      <div className="absolute inset-0" aria-hidden="true">
        <picture>
          {avifSrcSet && (
            <source srcSet={avifSrcSet} sizes={sizes} type="image/avif" />
          )}
          {webpSrcSet && (
            <source srcSet={webpSrcSet} sizes={sizes} type="image/webp" />
          )}
          {webpImage && !webpSrcSet && (
            <source srcSet={webpImage} type="image/webp" />
          )}
          <img
            src={image}
            alt=""
            role="presentation"
            width={1920}
            height={1080}
            decoding="async"
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : "auto"}
            className="w-full h-full object-cover object-center"
          />
        </picture>

        {videoSrc && showVideo && (
          <video
            src={videoSrc}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            disablePictureInPicture
            tabIndex={-1}
            aria-hidden="true"
            onPlaying={() => setVideoReady(true)}
            onError={() => setShowVideo(false)}
            className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-700 ${
              videoReady ? "opacity-100" : "opacity-0"
            }`}
          />
        )}
      </div>


      {overlay && (
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-b from-primary/50 via-primary/25 to-primary/65"
        />
      )}

      <div className="relative z-10 text-center px-4 sm:px-6 max-w-5xl mx-auto pt-24 pb-32 sm:py-12 w-full">
        <h1 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-primary-foreground leading-tight text-shadow-hero">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-4 sm:mt-6 font-body text-base sm:text-lg lg:text-xl text-primary-foreground max-w-3xl mx-auto leading-relaxed text-shadow-hero">
            {subtitle}
          </p>
        )}
        {children && <div className="mt-8">{children}</div>}
      </div>

      <AnimatedWaves />
    </section>
  );
};

export default HeroSection;
